import { LayoutGrid, List, Rows3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { SegmentedTabs } from '~/components/common/segmented-tabs';
import { Button } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';

import type { ListDensity, ListView } from '../store/list-view.store';

const DENSITIES: ListDensity[] = ['compact', 'normal', 'spacious'];

interface Props {
  view: ListView;
  density: ListDensity;
  onViewChange: (view: ListView) => void;
  onDensityChange: (density: ListDensity) => void;
  /** Product lists offer cards; plain tables (runs, schedules) offer density only. */
  allowCards?: boolean;
}

/**
 * List / Cards switch + Density menu shown at the right of every catalog-enrichment list toolbar.
 * Pair with `useListView(listKey)` so the choice is remembered per list.
 */
export function ListViewControls({
  view,
  density,
  onViewChange,
  onDensityChange,
  allowCards = true,
}: Props) {
  const { t } = useTranslation('catalog-enrichment');
  return (
    <div className="flex items-center gap-2">
      {allowCards && (
        <SegmentedTabs
          size="icon"
          value={view}
          onChange={onViewChange}
          ariaLabel={t('listView.viewLabel')}
          className="w-auto sm:max-w-none"
          options={[
            {
              value: 'list',
              ariaLabel: t('listView.list'),
              label: <List aria-hidden="true" className="size-4" />,
            },
            {
              value: 'cards',
              ariaLabel: t('listView.cards'),
              label: <LayoutGrid aria-hidden="true" className="size-4" />,
            },
          ]}
        />
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="cursor-pointer">
            <Rows3 aria-hidden="true" />
            {t(`listView.density.${density}`)}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>{t('listView.densityLabel')}</DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={density}
            onValueChange={(v) => {
              onDensityChange(v as ListDensity);
            }}
          >
            {DENSITIES.map((d) => (
              <DropdownMenuRadioItem key={d} value={d} className="cursor-pointer">
                {t(`listView.density.${d}`)}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
